"use client";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { CiCalendar, CiLocationOn } from "react-icons/ci";
import { GiCheckMark } from "react-icons/gi";
import { TbUsers } from "react-icons/tb";
import {
  formatPrice,
  gtagAvailable,
  montserrat,
  playfairDisplay,
  useLocalStorage,
  type IPriceCard,
} from "utils";
import { PriceCardPriceAndDiscount } from "./PriceCardPriceAndDiscount";
import { useAppConfiguration } from "appConfig";

export function PriceCard({
  startDate,
  endDate,
  numberOfParticipantsText,
  locationText,
  searchParams,
  handleNextStepChange,
  item,
  t,
  hideBookNow = false,
  eventLocationName = "",
  servicePackage,
}: IPriceCard) {
  const [selectedRow, setSelectedRow] = useState<number>(0);

  useEffect(() => {
    if (item && item.prices) {
      checkSelectedRow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  function getEventDate(start: string, end: string) {
    if (start === end) {
      return start;
    } else {
      return `${start} - ${end}`;
    }
  }

  const lock = useRef(true);
  const bookNowLockRef = useRef(false);
  const [reservationDraft, setDraft] = useLocalStorage<any>(
    "reservationDraft",
    null
  );
  useEffect(() => {
    if (!lock.current) {
      if (gtagAvailable()) {
        window.gtag!(
          "event",
          `selectedDeviceType: ${reservationDraft.reservation.type}`
        );
      }
      handleNextStepChange();
    }
    lock.current = true;
    // eslint-disable-next-line
  }, [reservationDraft]);

  function checkSelectedRow() {
    const prices = item.prices;
    const persons = Number(numberOfParticipantsText);
    for (let index = 0; index < prices.length; index++) {
      const participants = prices[index].participants;
      if (participants) {
        if (index - 1 >= 0) {
          const from = Number(prices[index - 1].participants) + 1;
          const to = Number(prices[index].participants);
          if (persons >= from && persons <= to) {
            setSelectedRow(index);
          }
        } else {
          const from = 0;
          const to = Number(prices[index].participants);
          if (persons >= from && persons <= to) {
            setSelectedRow(index);
          }
        }
      } else {
        const _index = index - 1 >= 0 ? index - 1 : index;
        const from = Number(prices[_index].participants) || 0;
        if (persons > from) {
          setSelectedRow(index);
        }
      }
    }
  }

  const { isMemoringApp } = useAppConfiguration();

  const buttonColor = useMemo(() => {
    return isMemoringApp
      ? "bg-memoringGold hover:bg-[#c28f42]"
      : "bg-purple hover:bg-[#735969]";
  }, [isMemoringApp]);

  return (
    <div className="w-full sm:max-w-[21rem] p-5 bg-white rounded-xl shadow-xl">
      <Image
        src={item.imgSrc}
        alt="Product"
        width={250}
        height={250}
        className="mx-auto"
      />
      <h2 className="flex flex-row justify-between items-center uppercase my-5">
        <span className={`${playfairDisplay.className} font-semibold text-xl`}>
          {item.type}
        </span>
        <span
          className={`${montserrat.className} text-xl leading-tight tracking-[5px]`}
        >
          {t("Global.design")}
        </span>
      </h2>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row flex-start items-center gap-2">
          <CiCalendar className="text-xl text-brownDarker" />
          <span className={`${montserrat.className} text-lg`}>
            {getEventDate(startDate, endDate)}
          </span>
        </div>
        <div className="flex flex-row flex-start items-center gap-2">
          <CiLocationOn className="text-xl text-brownDarker" />
          <span className={`${montserrat.className} text-lg`}>
            {locationText}
          </span>
        </div>
        <div className="flex flex-row flex-start items-center gap-2">
          <TbUsers className="text-xl text-brownDarker" />
          <span
            className={`${montserrat.className} text-lg`}
          >{`${numberOfParticipantsText} ${t("Prices.participants")}`}</span>
        </div>
      </div>

      <div className="flex flex-col justify-between mt-5">
        <PriceCardPriceAndDiscount item={item.prices[0]} t={t} />
        <div className="flex flex-col gap-3 h-[100px]">
          <p
            className={`${playfairDisplay.className} text-base text-brown font-semibold whitespace-nowrap`}
          >
            {t("Prices.depositCosts", {
              cost: item.prices[selectedRow]?.depositCost,
            })}
          </p>
          {servicePackage && item.prices[selectedRow]?.magicAddonCost ? (
            <p
              className={`${playfairDisplay.className} flex flex-row text-base text-brown justify-center items-center font-semibold w-fit md:whitespace-nowrap`}
            >
              <GiCheckMark className="text-base font-semibold mr-2 text-brown" />
              {t("Prices.magicAddonCostIncluded", {
                cost: formatPrice(
                  Number(item.prices[selectedRow]?.magicAddonCost),
                  item.prices[selectedRow].currency
                ),
              })}
            </p>
          ) : null}
          {Number(item.prices[selectedRow]?.shippingCost) > 0 ? (
            <p
              className={`${playfairDisplay.className} text-base text-center text-brown font-semibold max-w-[130px] whitespace-nowrap`}
            >
              {t("Prices.shippingCostPrice", {
                cost: formatPrice(
                  Number(item.prices[selectedRow].shippingCost),
                  item.prices[selectedRow].currency
                ),
              })}
            </p>
          ) : (
            <p
              className={`${playfairDisplay.className} flex flex-row text-base text-brown justify-center items-center font-semibold w-fit md:whitespace-nowrap`}
            >
              <GiCheckMark className="text-base font-semibold mr-2 text-brown" />
              {t("Prices.shippingIncluded")}
            </p>
          )}
        </div>
        {item.prices[selectedRow]?.vatIncluded ? (
          <p
            className={`${montserrat.className} text-xs text-gray-500 text-center mt-2`}
          >
            {t("Prices.inclVat")}
          </p>
        ) : null}
      </div>
      {!hideBookNow ? (
        <div className="mx-auto mt-5">
          <button
            type="button"
            disabled={item?.allIsBooked}
            className={`${
              item?.allIsBooked ? "btn-disabled" : buttonColor
            } text-white uppercase font-bold py-2 px-4 w-full rounded-full`}
            onClick={() => {
              if (bookNowLockRef.current) {
                return;
              }
              bookNowLockRef.current = true;
              lock.current = false;
              setDraft((prev: any) => ({
                ...prev,
                reservation: {
                  ...prev?.reservation,
                  ...item,
                  selectedRow,
                  ...searchParams,
                  hidePhoneCard: false,
                  ...searchParams?.cityAddress,
                  eventStartDay: startDate,
                  searchParams: { validSearch: searchParams?.validSearch },
                  eventEndDay: endDate,
                  eventLocationName,
                  magicServices: searchParams.servicePackage ? "MAGIC AI" : "",
                },
              }));
            }}
          >
            {t(item?.allIsBooked ? "Buttons.notAvailable" : "Buttons.select")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
